import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const RevenueAnalytics = ({ billingRecords, dateRange }) => {
  // Process billing records for chart data
  const processChartData = () => {
    const dataMap = new Map();

    billingRecords?.forEach(record => {
      const date = new Date(record?.billing_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const amount = parseFloat(record?.total_amount || 0);

      if (dataMap?.has(date)) {
        dataMap?.set(date, dataMap?.get(date) + amount);
      } else {
        dataMap?.set(date, amount);
      }
    });

    return Array.from(dataMap?.entries())?.map(([date, amount]) => ({ date, amount }))?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const chartData = processChartData();

  const formatCurrency = (value) => `$${value?.toFixed(2)}`;

  // Calculate payment status distribution
  const paymentStatusData = [
    {
      status: 'Completed',
      count: billingRecords?.filter(r => r?.payment_status === 'completed')?.length || 0,
      color: 'var(--color-success)'
    },
    {
      status: 'Pending',
      count: billingRecords?.filter(r => r?.payment_status === 'pending')?.length || 0,
      color: 'var(--color-warning)'
    },
    {
      status: 'Failed',
      count: billingRecords?.filter(r => r?.payment_status === 'failed')?.length || 0,
      color: 'var(--color-destructive)'
    }
  ];

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-heading font-bold text-foreground">
          Revenue Analytics
        </h2>
      </div>

      {/* Revenue Trend Chart */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Revenue Trend</h3>
        {chartData?.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="date"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                tickFormatter={formatCurrency}
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Line
                type="monotone"
                dataKey="amount"
                stroke="var(--color-primary)"
                strokeWidth={2}
                dot={{ fill: 'var(--color-primary)', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px] bg-muted/30 rounded-lg">
            <p className="text-muted-foreground text-sm">No revenue data available</p>
          </div>
        )}
      </div>

      {/* Payment Status Distribution */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Payment Status</h3>
        <div className="space-y-3">
          {paymentStatusData?.map((item) => (
            <div key={item?.status}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-foreground">{item?.status}</span>
                <span className="text-sm font-semibold text-foreground">{item?.count}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${(item?.count / billingRecords?.length) * 100}%`,
                    backgroundColor: item?.color
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-success/5 rounded-lg border border-success/20">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="DollarSign" size={16} color="var(--color-success)" />
            <p className="text-xs text-muted-foreground">Total Revenue</p>
          </div>
          <p className="text-lg font-bold text-success">
            ${billingRecords?.reduce((sum, r) => sum + parseFloat(r?.total_amount || 0), 0)?.toFixed(2)}
          </p>
        </div>

        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Receipt" size={16} color="var(--color-primary)" />
            <p className="text-xs text-muted-foreground">Transactions</p>
          </div>
          <p className="text-lg font-bold text-primary">
            {billingRecords?.length || 0}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;